import { filter, find, findIndex } from "lodash/fp";
import { create } from "zustand";

import STEPS from "@app/analytics/onboarding/steps";
import { getStepIndex } from "@app/analytics/onboarding/utils";
import * as Arrays from "@app/utils/arrays";

import {
  OnboardingContext,
  OnboardingStepContext,
  StepId,
  StepState,
  StepStatus,
} from "../types";

interface OnboardingStateValues {
  activeStepIndex: number;
  context: OnboardingContext;
  steps: StepState[];
}

interface OnboardingState extends OnboardingStateValues {
  initialize: (context: OnboardingStepContext) => void;
  refresh: (context: OnboardingStepContext) => void;
  reset: () => void;

  setActiveStep: (stepId: StepId) => void;
  goToNextStep: (status: Exclude<StepStatus, "pending">) => void;
  goToPrevStep: () => void;
}

const DEFAULT_CONTEXT: OnboardingContext = {
  entryPoint: null,
  source: "default",
  workspaceInvite: null,
};

const INITIAL_STATE_VALUES: OnboardingStateValues = {
  activeStepIndex: -1,
  context: DEFAULT_CONTEXT,
  steps: [],
};

const getInitialState = (context: OnboardingStepContext) => {
  const enabledSteps = filter((s) => s.isEnabled(context), STEPS);

  const steps: StepState[] = enabledSteps.map((step) => ({
    ...step,
    status: step.getInitialStatus(context),
  }));

  const entryPoint = context.entryPoint;

  const activeStepIndex = Math.max(
    0,
    entryPoint ? getStepIndex(steps, entryPoint) : 0
  );

  return { activeStepIndex, context, steps };
};

const updateSteps = (
  state: OnboardingState,
  context: OnboardingStepContext
): Partial<OnboardingStateValues> => {
  const enabledSteps = filter((s) => s.isEnabled(context), STEPS);

  const steps: StepState[] = enabledSteps.map((step) => {
    const existingStepState = find({ id: step.id }, state.steps)?.status;
    return {
      ...step,
      status: existingStepState ?? step.getInitialStatus(context),
    };
  });

  return { steps };
};

const updateStepStatus = (
  steps: StepState[],
  stepIndex: number,
  status: StepState["status"]
) => {
  const currentStep = steps[stepIndex];
  if (!currentStep || currentStep.status === status) return steps;

  const newStep = { ...currentStep, status };
  return Arrays.replaceAt(steps, newStep, stepIndex);
};

const handleNextStep = (
  state: OnboardingState,
  status: "complete" | "skipped"
): Partial<OnboardingState> => {
  const updatedSteps = updateStepStatus(
    state.steps,
    state.activeStepIndex,
    status
  );

  return {
    steps: updatedSteps,
    activeStepIndex: Math.min(
      state.activeStepIndex + 1,
      state.steps.length - 1
    ),
  };
};

const handlePrevStep = (state: OnboardingState): Partial<OnboardingState> => ({
  activeStepIndex: Math.max(0, state.activeStepIndex - 1),
});

const handleSetActiveStep = (stepId: StepId, state: OnboardingState) => {
  const index = findIndex({ id: stepId }, state.steps);
  return { activeStepIndex: Math.max(0, index) };
};

const useOnboardingStore = create<OnboardingState>((set) => ({
  ...INITIAL_STATE_VALUES,
  initialize: (context) => set(getInitialState(context)),
  refresh: (context) => set((state) => updateSteps(state, context)),

  reset: () => set(INITIAL_STATE_VALUES),

  setActiveStep: (stepId) => set((state) => handleSetActiveStep(stepId, state)),
  goToNextStep: (status = "complete") =>
    set((state) => handleNextStep(state, status)),
  goToPrevStep: () => set((state) => handlePrevStep(state)),
}));

export default useOnboardingStore;
